import { DIFF_LEVELS, templateForGridAndLevel } from "../../../admin/story/adminSlidingUi";
import {
    JIGSAW_PLAY_TILE_SIZES,
    pickDefaultJigsawPlayTemplate,
    pickJigsawTemplateForTileSize,
    pickPrimaryJigsawSeed,
    resolveJigsawTemplateTileSize,
} from "../jigsaw/jigsawBrowseUtils";

const STORAGE_KEY = "puzzle.playPrefs";

const SLIDING_GRID_SIZES = [4, 6];

function readStore() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function writeStore(store) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        /* ignore quota / private mode */
    }
}

function normalizeUserId(cloudUserId) {
    const id = String(cloudUserId ?? "").trim();
    return id || null;
}

/** Default selection: Easy 4×4 when present; else 4×4 Medium; else 6×6 Medium; else first matching size×level. */
export function resolveDefaultSlidingSelection(templates) {
    if (!Array.isArray(templates) || templates.length === 0) {
        return { gridSize: 4, level: "EASY" };
    }
    if (templateForGridAndLevel(templates, 4, "EASY")) {
        return { gridSize: 4, level: "EASY" };
    }
    if (templateForGridAndLevel(templates, 4, "MEDIUM")) {
        return { gridSize: 4, level: "MEDIUM" };
    }
    if (templateForGridAndLevel(templates, 6, "MEDIUM")) {
        return { gridSize: 6, level: "MEDIUM" };
    }
    for (const gs of SLIDING_GRID_SIZES) {
        for (const level of DIFF_LEVELS) {
            if (templateForGridAndLevel(templates, gs, level)) {
                return { gridSize: gs, level };
            }
        }
    }
    return { gridSize: 4, level: "EASY" };
}

export function loadSlidingPlayPrefs(cloudUserId) {
    const userId = normalizeUserId(cloudUserId);
    if (!userId) return null;
    const entry = readStore()[userId]?.sliding;
    if (!entry || typeof entry !== "object") return null;
    const gridSize = Number(entry.gridSize);
    const level = String(entry.level ?? "").toUpperCase();
    if (!SLIDING_GRID_SIZES.includes(gridSize)) return null;
    if (!DIFF_LEVELS.includes(level)) return null;
    return { gridSize, level };
}

export function saveSlidingPlayPrefs(cloudUserId, { gridSize, level }) {
    const userId = normalizeUserId(cloudUserId);
    if (!userId) return;
    const gs = Number(gridSize);
    const lv = String(level ?? "").toUpperCase();
    if (!SLIDING_GRID_SIZES.includes(gs) || !DIFF_LEVELS.includes(lv)) return;
    const store = readStore();
    store[userId] = { ...store[userId], sliding: { gridSize: gs, level: lv } };
    writeStore(store);
}

/** Logged-in default: saved grid/level when a matching template exists; else site default. */
export function resolveSlidingPlaySelection(templates, cloudUserId) {
    const saved = loadSlidingPlayPrefs(cloudUserId);
    if (saved && templateForGridAndLevel(templates, saved.gridSize, saved.level)) {
        return saved;
    }
    return resolveDefaultSlidingSelection(templates);
}

export function resolveSlidingDefaultTemplateId(templates, cloudUserId) {
    const { gridSize, level } = resolveSlidingPlaySelection(templates, cloudUserId);
    const tpl = templateForGridAndLevel(templates, gridSize, level);
    return tpl?.id ?? null;
}

export function loadJigsawPlayPrefs(cloudUserId) {
    const userId = normalizeUserId(cloudUserId);
    if (!userId) return null;
    const entry = readStore()[userId]?.jigsaw;
    if (!entry || typeof entry !== "object") return null;
    const tileSize = Number(entry.tileSize);
    if (!JIGSAW_PLAY_TILE_SIZES.includes(tileSize)) return null;
    return { tileSize };
}

export function saveJigsawPlayPrefs(cloudUserId, { tileSize }) {
    const userId = normalizeUserId(cloudUserId);
    if (!userId) return;
    const ts = Number(tileSize);
    if (!JIGSAW_PLAY_TILE_SIZES.includes(ts)) return;
    const store = readStore();
    store[userId] = { ...store[userId], jigsaw: { tileSize: ts } };
    writeStore(store);
}

/** Logged-in default: saved tile size when linked on this story; else smallest linked size. */
export function resolveJigsawPlayTemplate(templates, cloudUserId) {
    const list = Array.isArray(templates) ? templates : [];
    if (list.length === 0) return null;
    const saved = loadJigsawPlayPrefs(cloudUserId);
    if (saved?.tileSize) {
        const primarySeed = pickPrimaryJigsawSeed(list);
        const match = pickJigsawTemplateForTileSize(list, saved.tileSize, primarySeed);
        if (match) return match;
    }
    return pickDefaultJigsawPlayTemplate(list);
}

export function saveJigsawPrefsFromTemplate(cloudUserId, template) {
    if (!template) return;
    const tileSize = resolveJigsawTemplateTileSize(template);
    if (tileSize > 0) {
        saveJigsawPlayPrefs(cloudUserId, { tileSize });
    }
}

export function saveSlidingPrefsFromTemplate(cloudUserId, template) {
    if (!template) return;
    const gridSize = Number(template.gridSize ?? template.grid_size);
    const level = String(template.difficultyLevel ?? template.difficulty_level ?? "").toUpperCase();
    if (SLIDING_GRID_SIZES.includes(gridSize) && DIFF_LEVELS.includes(level)) {
        saveSlidingPlayPrefs(cloudUserId, { gridSize, level });
    }
}
